/* eslint-disable valid-jsdoc */
import Axios, { AxiosRequestConfig } from "axios";
import { handleLogout } from "helper";
import { keyConstants } from "helper/keyConstants";
import { BaseResponse } from "interface/baseResponse";

Axios.interceptors.response.use(
  (config) => {
    return config;
  },
  (error) => {
    if (error.response?.data?.message === "Auth token expired/invalid") {
      handleLogout();
    }
    return Promise.reject(error);
  }
);

const Request = {
  getUserToken() {
    let token = localStorage.getItem(keyConstants.AUTH_TOKEN) ?? "";

    if (!token) {
      return "";
    }

    return token;
  },
  /**
   * Prepares request options
   *
   * @param {AxiosRequestConfig} axiosOpts
   * @param {string} authToken
   * @return {Object}
   */
  prepareOptions(axiosOpts?: AxiosRequestConfig | null, authToken?: string) {
    /* eslint-disable-next-line */
    const { url, method, headers, data, ...requestOptions } = axiosOpts || {};

    const options = {
      ...requestOptions,
      headers: {
        ...(headers || {}),
        authorization: "Bearer " + authToken,
      },
    };

    return { data, ...options };
  },
  async request<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    return (
      await Axios.request<T>({
        url: endpoint,
        ...options,
      })
    );
  },
  /**
   * Makes a GET request
   *
   * @param {string} endpoint
   * @param {AxiosRequestConfig | null} options
   */
  async get<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    return (await Axios.get<BaseResponse<T>>(endpoint, options || {})).data;
  },
  /**
   * Makes a POST request
   *
   * @param endpoint
   * @param data
   * @param options
   */
  async post<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    let postData;
    let requestOptions;

    if (options) {
      const { data, ...rest } = options;

      postData = data;
      requestOptions = rest;
    }

    return (
      await Axios.post<BaseResponse<T>>(endpoint, postData, requestOptions)
    ).data;
  },
  /**
   * Makes a POST request
   *
   * @param endpoint
   * @param data
   * @param options
   */
  async put<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    let postData;
    let requestOptions;

    if (options) {
      const { data, ...rest } = options;

      postData = data;
      requestOptions = rest;
    }

    return (
      await Axios.put<BaseResponse<T>>(endpoint, postData, requestOptions)
    ).data;
  },
  /**
   * Makes a POST request
   *
   * @param endpoint
   * @param data
   * @param options
   */
  async patch<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    let postData;
    let requestOptions;

    if (options) {
      const { data, ...rest } = options;

      postData = data;
      requestOptions = rest;
    }

    return (
      await Axios.patch<BaseResponse<T>>(endpoint, postData, requestOptions)
    ).data;
  },
  /**
   * Makes a GET request
   *
   * @param endpoint
   * @param options
   */
  async delete<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    return (await Axios.delete<BaseResponse<T>>(endpoint, options || {})).data;
  },
  async requestWithUserToken<T = any>(endpoint: string, options?: AxiosRequestConfig | null) {
    const opts = this.prepareOptions(options, this.getUserToken());
    return await this.request<T>(endpoint, opts);
  },
  /**
   * Makes a POST request to a service with service token
   *
   * @param endpoint
   * @param options
   */
  async postWithUserToken<T = any>(
    endpoint: string,
    options?: AxiosRequestConfig | null
  ) {
    const opts = this.prepareOptions(options, this.getUserToken());

    return await this.post<T>(endpoint, opts);
  },
  /**
   * Makes a POST request to a service with service token
   *
   * @param endpoint
   * @param options
   */
  async putWithUserToken<T = any>(
    endpoint: string,
    options?: AxiosRequestConfig | null
  ) {
    const opts = this.prepareOptions(options, this.getUserToken());

    return await this.put<T>(endpoint, opts);
  },
  /**
   * Makes a POST request to a service with service token
   *
   * @param endpoint
   * @param options
   */
  async patchWithUserToken<T = any>(
    endpoint: string,
    options?: AxiosRequestConfig | null
  ) {
    const opts = this.prepareOptions(options, this.getUserToken());

    return await this.patch<T>(endpoint, opts);
  },
  /**
   * Makes a GET request to a service with service token
   *
   * @param endpoint
   * @param options
   */
  async getWithUserToken<T = any>(
    endpoint: string,
    options?: AxiosRequestConfig | null
  ) {
    const opts = this.prepareOptions(options, this.getUserToken());

    return await this.get<T>(endpoint, opts);
  },
  /**
   * Makes a GET request to a service with service token
   *
   * @param endpoint
   * @param options
   */
  async deleteWithUserToken<T = any>(
    endpoint: string,
    options?: AxiosRequestConfig | null
  ) {
    const opts = this.prepareOptions(options, this.getUserToken());

    return await this.delete<T>(endpoint, opts);
  },
};

export default Request;
